// TO DEFINE THE STATES AND CHANGE STATUS
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchShift,
  fetchVenueShifts,
  fetchEmployeeShifts,
  createNewShift,
  updateShift,
  deleteShift,
  autoStaffShifts,
  startShiftThunk,
  endShiftThunk,
  publishShiftsThunk,
  unpublishShiftsThunk,
} from './shiftsThunks';
import { LOADING_STATES } from '../../utils/constants/loadingStates';
import { SHIFT_STATUS } from '../../utils/constants/shiftConstants';
import { startBreakThunk, endBreakThunk } from './shiftsThunks';
import { subscribeToVenueShifts } from './shiftsService';

// Initial Redux state for shifts
const initialState = {
  shift: [],
  shifts: [], // All shifts in the venue
  employeeShifts: [], // Shifts specific to the current employee
  selectedShiftId: null,
  selectedShift: null,
  // Daily notes state
  dailyNotes: {
    employee: {}, // Keyed by date YYYY-MM-DD
    manager: {}, // Keyed by date YYYY-MM-DD
  },
  // Statuses
  status: LOADING_STATES.LOADING,
  autoStaffingStatus: LOADING_STATES.IDLE,
  updatedStatus: LOADING_STATES.IDLE,
  changeClockStatus: LOADING_STATES.IDLE,
  breakActionStatus: LOADING_STATES.IDLE,
  updatedPublishStatus: LOADING_STATES.IDLE,
  updatedUnpublishStatus: LOADING_STATES.IDLE,
  period: {
    start: null,
    end: null,
  },
  error: null,
  changeClockError: null,
};

// Utility function to deduplicate shifts with the same id (useful for optimistic updates combined with real-time listener)
function dedupeShiftsById(shifts) {
  const map = new Map();
  shifts.forEach((shift) => {
    if (shift && shift.id) {
      map.set(shift.id, shift);
    }
  });
  return Array.from(map.values());
}

const shiftsSlice = createSlice({
  name: 'newShifts',
  initialState,
  reducers: {
    setSelectedShiftId: (state, action) => {
      state.selectedShiftId = action.payload;
    },
    setSelectedPeriod: (state, action) => {
      state.period = action.payload;
    },
    setSelectedShift: (state, action) => {
      state.selectedShift = action.payload;
    },
    setShiftsFromSnapshot: (state, action) => {
      state.shifts = dedupeShiftsById(action.payload);
      state.status = LOADING_STATES.SUCCEEDED;
    },
    setEmployeeShiftsFromSnapshot: (state, action) => {
      state.employeeShifts = action.payload;
      state.status = LOADING_STATES.SUCCEEDED;
    },
    updateDailyNotes: (state, action) => {
      const { type, notes, date } = action.payload;
      if (!state.dailyNotes) {
        state.dailyNotes = {
          employee: {},
          manager: {},
        };
      }
      if (!state.dailyNotes[type]) {
        state.dailyNotes[type] = {};
      }
      state.dailyNotes[type][date] = notes;
    },

    updateShiftClockStatus: (state, action) => {
      const { id, status } = action.payload;
      const shift = state.shifts.find((s) => s.id === id);
      if (shift) {
        shift.status = status;
      }
      const employeeShift = state.employeeShifts.find((s) => s.id === id);
      if (employeeShift) {
        employeeShift.status = status;
      }
    },
    // Add optimistic shift swap update
    updateShiftWithPendingSwap: (state, action) => {
      const { shiftId, pendingSwapId, selectedEmployee } = action.payload;

      // Update in shifts array
      const shift = state.shifts.find((s) => s.id === shiftId);
      if (shift) {
        shift.pendingSwap = pendingSwapId;
        if (selectedEmployee) {
          shift.selectedEmployee = selectedEmployee;
        }
      }

      // Update in employeeShifts array
      const employeeShift = state.employeeShifts.find((s) => s.id === shiftId);
      if (employeeShift) {
        employeeShift.pendingSwap = pendingSwapId;
        if (selectedEmployee) {
          employeeShift.selectedEmployee = selectedEmployee;
        }
      }

      // Update selectedShift if it's the same shift
      if (state.selectedShift && state.selectedShift.id === shiftId) {
        state.selectedShift.pendingSwap = pendingSwapId;
        if (selectedEmployee) {
          state.selectedShift.selectedEmployee = selectedEmployee;
        }
      }
    },
    // Clear pending swap when request fails
    clearShiftPendingSwap: (state, action) => {
      const { shiftId } = action.payload;

      // Clear in shifts array
      const shift = state.shifts.find((s) => s.id === shiftId);
      if (shift) {
        delete shift.pendingSwap;
        delete shift.selectedEmployee;
      }

      // Clear in employeeShifts array
      const employeeShift = state.employeeShifts.find((s) => s.id === shiftId);
      if (employeeShift) {
        delete employeeShift.pendingSwap;
        delete employeeShift.selectedEmployee;
      }

      // Clear in selectedShift if it's the same shift
      if (state.selectedShift && state.selectedShift.id === shiftId) {
        delete state.selectedShift.pendingSwap;
        delete state.selectedShift.selectedEmployee;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH A SINGLE SHIFT
      .addCase(fetchShift.pending, (state) => {
        state.updatedStatus = LOADING_STATES.LOADING;
      })
      .addCase(fetchShift.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.updatedStatus = action.payload;
        state.selectedShift = action.payload;
        // Update the shift in the shifts array if it exists
        state.shifts = state.shifts.map((shift) => (shift.id === action.payload.id ? action.payload : shift));
      })
      .addCase(fetchShift.rejected, (state, action) => {
        state.updatedStatus = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // FETCH VENUE SHIFTS
      .addCase(fetchVenueShifts.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(fetchVenueShifts.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.shifts = action.payload.shifts;

        // If we have an employeeId in the payload, also update employeeShifts
        if (action.payload.employeeId) {
          state.employeeShifts = action.payload.shifts.filter((shift) => shift.eid === action.payload.employeeId);
        }
      })
      .addCase(fetchVenueShifts.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // FETCH EMPLOYEE SHIFTS
      .addCase(fetchEmployeeShifts.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(fetchEmployeeShifts.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.employeeShifts = action.payload.shifts;
      })
      .addCase(fetchEmployeeShifts.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // CREATE NEW SHIFT
      .addCase(createNewShift.pending, (state) => {
        state.updatedStatus = LOADING_STATES.LOADING;
      })
      .addCase(createNewShift.fulfilled, (state, action) => {
        state.updatedStatus = LOADING_STATES.SUCCEEDED;
        state.selectedShift = action.payload;
        state.shifts = dedupeShiftsById([...state.shifts, action.payload]);
      })
      .addCase(createNewShift.rejected, (state, action) => {
        state.updatedStatus = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // UPDATE SHIFT
      .addCase(updateShift.pending, (state) => {
        state.updatedStatus = LOADING_STATES.LOADING;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.updatedStatus = LOADING_STATES.SUCCEEDED;
        state.selectedShift = action.payload;
        // Update the shift in the shifts array
        state.shifts = state.shifts.map((shift) => (shift.id === action.payload.id ? action.payload : shift));
      })
      .addCase(updateShift.rejected, (state, action) => {
        state.updatedStatus = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // DELETE SHIFT
      .addCase(deleteShift.pending, (state) => {
        state.updatedStatus = LOADING_STATES.LOADING;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.updatedStatus = LOADING_STATES.SUCCEEDED;
        // Remove the shift from the shifts array
        state.shifts = state.shifts.filter((shift) => shift.id !== action.payload);
        // Clear the selected shift if it was the one that was deleted
        if (state.selectedShift && state.selectedShift.id === action.payload) {
          state.selectedShift = null;
        }
      })
      .addCase(deleteShift.rejected, (state, action) => {
        state.updatedStatus = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // AUTO-STAFFING
      .addCase(autoStaffShifts.pending, (state) => {
        state.autoStaffingStatus = LOADING_STATES.LOADING;
        state.error = null;
      })
      .addCase(autoStaffShifts.fulfilled, (state, action) => {
        state.autoStaffingStatus = LOADING_STATES.SUCCEEDED;
        // Update the shifts in the store if they/array were returned
        if (action.payload?.shifts) {
          state.shifts = dedupeShiftsById(action.payload.shifts);
        }
      })
      .addCase(autoStaffShifts.rejected, (state, action) => {
        state.autoStaffingStatus = LOADING_STATES.FAILED;
        state.error = action.payload;
      })
      // Start Shift - Updates Redux state after successful clock in
      .addCase(startShiftThunk.pending, (state) => {
        state.changeClockError = null;
        state.changeClockStatus = LOADING_STATES.LOADING;
      })
      .addCase(startShiftThunk.fulfilled, (state, action) => {
        state.changeClockStatus = LOADING_STATES.SUCCEEDED;

        // Update the shift in the main shifts array
        const shift = state.shifts.find((shift) => shift.id === action.payload.id);
        if (shift) {
          Object.assign(shift, action.payload);
        }

        // Update the shift in the employee-specific shifts array
        const employeeShift = state.employeeShifts.find((shift) => shift.id === action.payload.id);
        if (employeeShift) {
          Object.assign(employeeShift, action.payload);
        }
      })
      .addCase(startShiftThunk.rejected, (state, action) => {
        state.changeClockStatus = LOADING_STATES.FAILED;
        state.changeClockError = action.error.message;
      })
      // End Shift - Updates Redux state after successful clock out
      .addCase(endShiftThunk.pending, (state) => {
        state.changeClockError = null;
        state.changeClockStatus = LOADING_STATES.LOADING;
      })
      .addCase(endShiftThunk.fulfilled, (state, action) => {
        state.changeClockStatus = LOADING_STATES.SUCCEEDED;

        // Update the shift in the main shifts array
        const shift = state.shifts.find((shift) => shift.id === action.payload.id);
        if (shift) {
          Object.assign(shift, action.payload);
        }

        // Update the shift in the employee-specific shifts array
        const employeeShift = state.employeeShifts.find((shift) => shift.id === action.payload.id);
        if (employeeShift) {
          Object.assign(employeeShift, action.payload);
        }
      })
      .addCase(endShiftThunk.rejected, (state, action) => {
        state.changeClockStatus = LOADING_STATES.FAILED;
        state.changeClockError = action.error.message;
      })

      // Start Break - Updates Redux state after successful break start
      .addCase(startBreakThunk.pending, (state) => {
        state.changeClockError = null;
        state.breakActionStatus = LOADING_STATES.LOADING;
      })
      .addCase(startBreakThunk.fulfilled, (state, action) => {
        state.breakActionStatus = LOADING_STATES.SUCCEEDED;

        // Update the shift in both arrays
        const updateShiftInArray = (shift) => {
          if (shift && shift.id === action.payload.id) {
            Object.assign(shift, action.payload);
            // Set the correct break status
            shift.status = action.payload.breakType === 'MEAL_BREAK' ? SHIFT_STATUS.ON_BREAK : SHIFT_STATUS.ON_REST;
          }
        };

        // Update in main shifts array
        state.shifts.forEach(updateShiftInArray);
        // Update in employee shifts array
        state.employeeShifts.forEach(updateShiftInArray);
      })
      .addCase(startBreakThunk.rejected, (state, action) => {
        state.breakActionStatus = LOADING_STATES.FAILED;
        state.changeClockError = action.error.message;
      })

      // End Break - Updates Redux state after successful break end
      .addCase(endBreakThunk.pending, (state) => {
        state.changeClockError = null;
        state.breakActionStatus = LOADING_STATES.LOADING;
      })
      .addCase(endBreakThunk.fulfilled, (state, action) => {
        state.breakActionStatus = LOADING_STATES.SUCCEEDED;

        // Update the shift in both arrays
        const updateShiftInArray = (shift) => {
          if (shift && shift.id === action.payload.id) {
            Object.assign(shift, action.payload);
            // Reset status back to in progress
            shift.status = SHIFT_STATUS.IN_PROGRESS;
          }
        };

        // Update in main shifts array
        state.shifts.forEach(updateShiftInArray);
        // Update in employee shifts array
        state.employeeShifts.forEach(updateShiftInArray);
      })
      .addCase(endBreakThunk.rejected, (state, action) => {
        state.breakActionStatus = LOADING_STATES.FAILED;
        state.changeClockError = action.error.message;
      })
      // Publish Shifts
      .addCase(publishShiftsThunk.pending, (state) => {
        state.updatedPublishStatus = LOADING_STATES.LOADING;
      })
      .addCase(publishShiftsThunk.fulfilled, (state, action) => {
        state.updatedPublishStatus = LOADING_STATES.SUCCEEDED;
      })
      .addCase(publishShiftsThunk.rejected, (state, action) => {
        state.updatedPublishStatus = LOADING_STATES.FAILED;
        state.error = action.error.message;
      })
      // Unpublish Shifts
      .addCase(unpublishShiftsThunk.pending, (state) => {
        state.updatedUnpublishStatus = LOADING_STATES.LOADING;
      })
      .addCase(unpublishShiftsThunk.fulfilled, (state, action) => {
        state.updatedUnpublishStatus = LOADING_STATES.SUCCEEDED;
      })
      .addCase(unpublishShiftsThunk.rejected, (state, action) => {
        state.updatedUnpublishStatus = LOADING_STATES.FAILED;
        state.error = action.error.message;
      });
  },
});

// Add the new selector
export const selectDailyNotes = (state, type, date) => {
  return state.newShifts?.dailyNotes?.[type]?.[date] || '';
};

export const {
  setSelectedShiftId,
  setSelectedPeriod,
  setSelectedShift,
  setShiftsFromSnapshot,
  setEmployeeShiftsFromSnapshot,
  updateDailyNotes,
  updateShiftClockStatus,
  updateShiftWithPendingSwap,
  clearShiftPendingSwap,
} = shiftsSlice.actions;

//selector to get 1 shift by his id
export const selectShiftById = (state) => state.newShifts.shift;

//selector to get 1 shift by his id
export const selectSelectedShift = (state) => state.newShifts.selectedShift;

//selector to get the period of the current week
export const selectPeriod = (state) => state.newShifts.period;

//selector to get all shifts
export const selectAllShifts = (state) => state.newShifts.shifts;

//selector to get employee-specific shifts
export const selectEmployeeShifts = (state) => state.newShifts.employeeShifts;

//selector to get the selected shift id
export const selectShiftId = (state) => state.newShifts.selectedShiftId;

//selector to get the status of the shifts
export const selectShiftStatus = (state) => state.newShifts.status;

//selector to get the status of the auto-staffing
export const selectAutoStaffingStatus = (state) => state.newShifts.autoStaffingStatus;

//selector to get the status of the updated shift
export const selectUpdatedStatus = (state) => state.newShifts.updatedStatus;

//selector to get the status of the publish shifts
export const selectUpdatedPublishStatus = (state) => state.newShifts.updatedPublishStatus;

//selector to get the status of the unpublish shifts
export const selectUpdatedUnpublishStatus = (state) => state.newShifts.updatedUnpublishStatus;

//selector to get the status of the change clock
export const selectChangeClockStatus = (state) => state.newShifts.changeClockStatus;

//selector to get the status of break actions
export const selectBreakActionStatus = (state) => state.newShifts.breakActionStatus;

//selector to get the error of the change clock
export const selectChangeClockError = (state) => state.newShifts.changeClockError;

// Real-time listener management for venue shifts
const createVenueShiftsListener = () => {
  let unsubscribeShifts = null;
  return {
    start: (venueId, period, employeeId, dispatch) => {
      if (unsubscribeShifts) unsubscribeShifts();
      unsubscribeShifts = subscribeToVenueShifts(venueId, period, employeeId, (shifts) => {
        if (employeeId) {
          dispatch(setEmployeeShiftsFromSnapshot(shifts));
        } else {
          dispatch(setShiftsFromSnapshot(shifts));
        }
      });
    },
    stop: () => {
      if (unsubscribeShifts) unsubscribeShifts();
      unsubscribeShifts = null;
    },
  };
};

const venueShiftsListener = createVenueShiftsListener();

export const startVenueShiftsListener = (venueId, period, employeeId) => (dispatch) => {
  venueShiftsListener.start(venueId, period, employeeId, dispatch);
};

export const stopVenueShiftsListener = () => (dispatch) => {
  venueShiftsListener.stop();
};

export default shiftsSlice;
